import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Check, ChevronDown, Lock } from 'lucide-react';
import { useState } from 'react';
import { DateInput } from '@/components/date-input';
import { ErrorBanner } from '@/components/form';
import { StatusChip } from '@/components/migration/file-drop';
import { ImportTransactionsCard } from '@/components/migration/qbo-history-card';
import { QuickBooksConnection } from '@/components/migration/quickbooks';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { formatDate } from '@/lib/format';
import { i18n, useT } from '@/lib/i18n';
import {
  MARKABLE,
  fmtAmount,
  STEPS,
  progress,
  stepStatusLabel,
  useMigration,
  type MigrationView,
  type Side,
  type StepStatus,
  type StepView,
} from '@/lib/migration';
import { toMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/settings/migration/')({ component: MoveFromQuickBooks });

const TONE: Record<StepStatus, 'success' | 'neutral' | 'warning' | 'info'> = {
  done: 'success',
  skipped: 'neutral',
  todo: 'info',
  attention: 'warning',
};

/** Screen G7 (document 06): the Move from QuickBooks checklist. */
function MoveFromQuickBooks() {
  const orgId = useOrgId();
  const me = useMe();
  const m = useMigration();
  const t = useT();
  return (
    <>
      <PageHeader
        title={t('settingsPages.migration.moveFromQuickBooks')}
        description={t('settingsPages.qboHub.description')}
        actions={
          <Button variant="outline" asChild>
            <Link to="/settings/import">{t('settingsPages.data.importHub.title')}</Link>
          </Button>
        }
      />
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />
      ) : m.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : m.isError ? (
        <EmptyState title={t('settingsPages.qboHub.couldNotLoad')} hint={m.error.message} />
      ) : (
        <div className="flex max-w-5xl flex-col gap-4">
          <QuickBooksConnection />
          <ImportTransactionsCard />
          <Checklist key={orgId} view={m.data} staff={Boolean(me.data?.isStaff)} />
        </div>
      )}
    </>
  );
}

function Checklist({ view, staff }: { view: MigrationView; staff: boolean }) {
  const p = progress(view.steps);
  const t = useT();
  return (
    <div className="flex max-w-5xl flex-col gap-4">
      <div className="flex items-center gap-3 text-sm" role="status">
        <div className="h-2 w-48 overflow-hidden rounded-full bg-muted" aria-hidden>
          <div
            className="h-full bg-primary"
            style={{ width: `${Math.round((p.done / p.total) * 100)}%` }}
          />
        </div>
        <span>{t('settingsPages.qboHub.stepsDone', { done: p.done, total: p.total })}</span>
        {view.status === 'finished' ? (
          <StatusChip tone="success">{t('settingsPages.qboHub.finished')}</StatusChip>
        ) : view.status === 'in_progress' ? (
          <StatusChip tone="info">{t('purchasePages.reconcile.statusInProgress')}</StatusChip>
        ) : null}
      </div>
      <ol className="flex flex-col divide-y rounded-lg border bg-card">
        {STEPS.map((meta, i) => {
          const step = view.steps.find((s) => s.key === meta.key)!;
          return (
            <li key={meta.key} className="flex gap-4 px-4 py-4" data-testid={`step-${meta.key}`}>
              <span
                className={cn(
                  'mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full border text-xs font-medium',
                  step.status === 'done' || step.status === 'skipped'
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'text-muted-foreground',
                )}
                aria-hidden
              >
                {step.status === 'done' || step.status === 'skipped' ? (
                  <Check className="size-4" />
                ) : (
                  i + 1
                )}
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{meta.title}</span>
                  <StatusChip tone={TONE[step.status]}>
                    {stepStatusLabel(step.status)}
                    {step.manual ? ` ${t('settingsPages.qboHub.marked')}` : ''}
                  </StatusChip>
                </div>
                <p className="text-sm">{step.detail}</p>
                <p className="text-xs text-muted-foreground">{meta.hint}</p>
                {meta.key === 'conversion' ? <ConversionDate view={view} staff={staff} /> : null}
                {meta.key === 'review' ? <Review view={view} staff={staff} /> : null}
              </div>
              <StepActions
                step={step}
                to={meta.to}
                staff={staff}
                started={view.status !== 'not_started'}
                finished={view.status === 'finished'}
              />
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function StepActions({
  step,
  to,
  staff,
  started,
  finished,
}: {
  step: StepView;
  to: string | undefined;
  staff: boolean;
  started: boolean;
  finished: boolean;
}) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const t = useT();
  const mark = useMutation({
    mutationFn: (status: 'done' | 'skipped' | 'open') =>
      api<MigrationView>(`/orgs/${orgId}/migration/steps/${step.key}`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      }),
    onSuccess: (v) => qc.setQueryData(['migration', orgId], v),
    onError: (e) => toast((e as Error).message, { tone: 'error' }),
  });
  const importStep = ['opening_balances', 'open_invoices', 'unpaid_bills'].includes(step.key);
  const blocked = !started || (finished && importStep);
  const label =
    step.key === 'parallel_run'
      ? t('settingsPages.qboHub.compare')
      : step.status === 'done' && importStep
        ? t('purchasePages.import.steps.review')
        : t('common.import');
  return (
    <div className="flex shrink-0 items-start gap-2">
      {to ? (
        blocked && step.key !== 'parallel_run' ? (
          <Button
            variant="outline"
            size="sm"
            disabled
            title={
              finished
                ? t('settingsPages.qboHub.moveFinished')
                : t('settingsPages.migration.setDateFirst')
            }
          >
            {label}
          </Button>
        ) : (
          <Button variant={step.status === 'done' ? 'outline' : 'default'} size="sm" asChild>
            <Link to={to}>{label}</Link>
          </Button>
        )
      ) : null}
      {staff && started && !finished && MARKABLE.includes(step.key) ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              aria-label={t('settingsPages.qboHub.moreFor', {
                // English keeps the step's key, as before; Arabic names the step
                step:
                  i18n.language === 'ar'
                    ? (STEPS.find((s) => s.key === step.key)?.title ?? step.key)
                    : step.key,
              })}
            >
              <ChevronDown className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => mark.mutate('done')}>
              {t('settingsPages.qboHub.markDone')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => mark.mutate('skipped')}>
              {t('settingsPages.qboHub.skip')}
            </DropdownMenuItem>
            {step.manual ? (
              <DropdownMenuItem onSelect={() => mark.mutate('open')}>
                {t('accountingPages.periodClose.reopen')}
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}

function ConversionDate({ view, staff }: { view: MigrationView; staff: boolean }) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const t = useT();
  const [date, setDate] = useState(view.conversionDate ?? '');
  const [editing, setEditing] = useState(!view.conversionDate);
  const save = useMutation({
    mutationFn: () =>
      api<MigrationView>(`/orgs/${orgId}/migration`, {
        method: 'PUT',
        body: JSON.stringify({ conversionDate: date }),
      }),
    onSuccess: (v) => {
      qc.setQueryData(['migration', orgId], v);
      setEditing(false);
      toast(t('settingsPages.qboHub.conversionDateSaved'));
    },
  });
  const fixed = Boolean(view.trialBalance) || view.status === 'finished';
  if (!staff) return null;
  if (!editing)
    return fixed ? null : (
      <div>
        <Button variant="link" className="h-auto p-0 text-sm" onClick={() => setEditing(true)}>
          {t('settingsPages.qboHub.changeConversionDate')}
        </Button>
      </div>
    );
  const notFirst = date !== '' && !date.endsWith('-01');
  return (
    <div className="mt-2 flex flex-col gap-2">
      <ErrorBanner>{save.error?.message}</ErrorBanner>
      <div className="flex items-end gap-2">
        <label className="flex flex-col gap-1 text-sm font-medium">
          {t('settingsPages.qboHub.conversionDate')}
          <DateInput
            value={date}
            onChange={setDate}
            aria-label={t('settingsPages.qboHub.conversionDate')}
            className="w-40"
            aria-invalid={notFirst}
          />
        </label>
        <Button
          size="sm"
          disabled={!date || notFirst || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? t('common.saving') : t('common.save')}
        </Button>
      </div>
      {notFirst ? (
        <p className="text-xs text-destructive">{t('settingsPages.qboHub.firstOfMonth')}</p>
      ) : null}
    </div>
  );
}

function Review({ view, staff }: { view: MigrationView; staff: boolean }) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const tr = useT();
  const finish = useMutation({
    mutationFn: () =>
      api<MigrationView>(`/orgs/${orgId}/migration/finish`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: (v) => {
      qc.setQueryData(['migration', orgId], v);
      void qc.invalidateQueries({ queryKey: ['periods', orgId] });
      toast(tr('settingsPages.qboHub.finishedToast', { date: formatDate(v.openingDate!) }));
    },
  });
  if (view.status === 'not_started') return null;
  return (
    <div className="mt-2 flex flex-col gap-3">
      <table className="w-full max-w-xl text-sm" aria-label={tr('settingsPages.qboHub.tieOut')}>
        <thead>
          <tr className="border-b text-start text-xs text-muted-foreground">
            <th className="py-1 font-medium">{tr('settingsPages.qboHub.controlAccount')}</th>
            <th className="py-1 text-end font-medium">
              {tr('reportPages.dashboard.parallelRun.report.trial_balance')}
            </th>
            <th className="py-1 text-end font-medium">
              {tr('settingsPages.migration.openItems.openItems')}
            </th>
            <th className="py-1 text-end font-medium">
              {tr('settingsPages.migration.openItems.difference')}
            </th>
          </tr>
        </thead>
        <tbody>
          {(['receivable', 'payable'] as Side[]).map((k) => {
            const t = view.tieOut[k];
            const off = (toMinor(t.difference) ?? 0n) !== 0n;
            return (
              <tr key={k} className={cn('border-b', off && 'bg-warning/10')}>
                <td className="py-1">{tr(`settingsPages.qboHub.tieRow.${k}`)}</td>
                <td className="tnum py-1 text-end">{fmtAmount(t.expected)}</td>
                <td className="tnum py-1 text-end">{fmtAmount(t.imported)}</td>
                <td className={cn('tnum py-1 text-end', off && 'font-semibold')}>
                  {fmtAmount(t.difference)}
                  {off ? (
                    <span className="sr-only"> {tr('settingsPages.qboHub.differs')}</span>
                  ) : null}
                </td>
              </tr>
            );
          })}
          <tr>
            <td className="py-1">
              {view.openingDate
                ? tr('settingsPages.qboHub.openingEquityAt', {
                    date: formatDate(view.openingDate),
                  })
                : tr('settingsPages.qboHub.openingEquity')}
            </td>
            <td />
            <td />
            <td className="tnum py-1 text-end">{fmtAmount(view.openingEquity)}</td>
          </tr>
        </tbody>
      </table>
      {view.warnings.map((w) => (
        <p key={w} className="text-sm text-warning">
          {w}
        </p>
      ))}
      <ErrorBanner>{finish.error?.message}</ErrorBanner>
      {staff && view.status !== 'finished' ? (
        <div className="flex items-center gap-3">
          <Button disabled={!view.canFinish || finish.isPending} onClick={() => finish.mutate()}>
            <Lock className="size-4" />
            {finish.isPending
              ? tr('purchasePages.reconcile.finishing')
              : tr('settingsPages.qboHub.finishLock', {
                  date: view.openingDate ? formatDate(view.openingDate) : '…',
                })}
          </Button>
          {!view.canFinish ? (
            <span className="text-xs text-muted-foreground">{view.blockers[0]}</span>
          ) : (
            <span className="text-xs text-muted-foreground">
              {tr('settingsPages.qboHub.needsCloseRights')}
            </span>
          )}
        </div>
      ) : null}
    </div>
  );
}
